/** RFC-227 §4.2/§4.4/§4.5 — period applications, wrap-up, signed evidence. */
import { useState } from 'react'
import { format } from 'date-fns'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Link,
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router'
import type { AccessReviewPeriodDTO } from '@deepguide-ai/types'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@deepguide-ai/ui/tooltip'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import { Progress } from '@deepguide-ai/ui/progress'
import { StatCard, STAT_CARD_VALUE_COLORS } from '@deepguide-ai/ui/stat-card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@deepguide-ai/ui/table'
import {
  AlertTriangle,
  Eye,
  FileCheck2,
  LoaderCircle,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  ShieldCheck,
  Trash2,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@deepguide-ai/ui/dropdown-menu'
import { toast } from 'sonner'
import { useSession } from '@/lib/auth-client'
import { usePageHeaderTitle } from '@/lib/page-header'
import { trpc } from '@/lib/trpc'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { HeaderField } from '@/components/compliance/access-reviews/HeaderField'
import { AppLogo } from '@/components/compliance/access-reviews/AppLogo'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import { InfoNote } from '@/components/compliance/access-reviews/InfoNote'
import { CustomRestConnectionButton } from '@/components/compliance/people/CustomRestIntegrationWizard'
import { AddCustomApplicationDialog } from '@/components/compliance/people/AddCustomApplicationDialog'
import { EvidencePackActionsSection } from '@/components/evidence/EvidencePackActionsSection'
import {
  PageHeaderBreadcrumb,
  PageHeaderExtra,
} from '@/components/layout/PageHeaderActions'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { EditAccessReviewDialog } from '@/components/compliance/access-reviews/EditAccessReviewDialog'
import { UserSelect } from '@/components/ui/user-select'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/reviews/access_/period/$periodKey'
)({
  component: AccessReviewPeriodPage,
  staticData: { pageHeader: { title: 'Access Review' } },
})

const DEFAULT_ATTESTATION =
  "I reviewed the applications in scope and all their account decisions, including the initial reviewers' own accounts, against the confirmed workforce. Decisions and exceptions are recorded in each application pack; denied or modified access is a desired-state change tracked separately to closure. This approval covers the listed applications; it does not establish that other systems are outside the organization's required scope."

function AccessReviewPeriodPage() {
  const { workspaceId, periodKey } = useParams({ strict: false }) as {
    workspaceId: string
    periodKey: string
  }
  const { canWorkspaceWrite } = useWorkspacePermissions(workspaceId)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const revisionMutation = useMutation({
    ...trpc.accessReview.revisePeriod.mutationOptions(),
    onError: (error: Error) => toast.error(error.message),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: trpc.accessReview.periods.queryKey() })
      await queryClient.invalidateQueries({ queryKey: trpc.accessReview.period.queryKey({ periodKey }) })
      await navigate({
        to: '/workspaces/$workspaceId/reviews/access/period/$periodKey',
        params: { workspaceId, periodKey: result.periodKey },
      })
    },
  })
  const deleteMutation = useMutation({
    ...trpc.accessReview.deletePeriod.mutationOptions(),
    onError: (error: Error) => toast.error(error.message),
    onSuccess: async () => {
      setDeleteOpen(false)
      toast.success('Review deleted')
      await queryClient.invalidateQueries({
        queryKey: trpc.accessReview.periods.queryKey(),
      })
      await navigate({
        to: '/workspaces/$workspaceId/reviews/access',
        params: { workspaceId },
      })
    },
  })
  const periodQuery = useQuery({
    ...trpc.accessReview.period.queryOptions({ periodKey }),
    refetchInterval: (query) => {
      const currentPeriod = query.state.data as
        | AccessReviewPeriodDTO
        | null
        | undefined
      return currentPeriod?.applicationReviews.some(isCollectingPopulation)
        ? 1_500
        : false
    },
  })
  const period = periodQuery.data as AccessReviewPeriodDTO | null | undefined
  usePageHeaderTitle(period?.label ?? null)
  const firstReviewId = period?.applicationReviews[0]?.id
  const workforceStatusQuery = useQuery({
    ...trpc.accessReview.workforceStatus.queryOptions({
      cycleId: firstReviewId ?? '',
    }),
    enabled: Boolean(firstReviewId),
    meta: { skipGlobalErrorHandler: true },
    retry: false,
    refetchOnWindowFocus: false,
  })
  const [workforceConfirmed, setWorkforceConfirmed] = useState(false)
  const [attestationExceptions, setAttestationExceptions] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  // The selected person must personally approve before this period is signed.
  const { data: session } = useSession()
  const [approverUserId, setApproverUserId] = useState('')
  const signerUserId = session?.user?.id ?? ''
  const completionMutation = useMutation({
    ...trpc.accessReview.requestPeriodApproval.mutationOptions(),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: trpc.accessReview.period.queryKey({ periodKey }),
      })
      await queryClient.invalidateQueries({
        queryKey: trpc.accessReview.periods.queryKey(),
      })
    },
    onError: (error: Error) => toast.error(error.message),
    onSuccess: () => {
      setConfirmOpen(false)
      toast.success('Approval requested')
    },
  })

  const [approvalNotes, setApprovalNotes] = useState('')
  const [returnOpen, setReturnOpen] = useState(false)
  const approveMutation = useMutation({
    ...trpc.accessReview.approvePeriod.mutationOptions(),
    onError: (error: Error) => toast.error(error.message),
    onSuccess: async () => {
      if (period) {
        const statusId = period.controlEvidenceStatusId
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.evidenceFile.list.queryKey({ statusId }),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.evidencePack.listForStatus.queryKey({ statusId }),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.guidedTemplate.getStatusById.queryKey({ id: statusId }),
          }),
        ])
      }
      toast.success('Review approved and signed')
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: trpc.accessReview.period.queryKey({ periodKey }),
      })
      await queryClient.invalidateQueries({
        queryKey: trpc.accessReview.periods.queryKey(),
      })
    },
  })
  const returnMutation = useMutation({
    ...trpc.accessReview.returnPeriodApproval.mutationOptions(),
    onError: (error: Error) => toast.error(error.message),
    onSuccess: (_result, input) => { setReturnOpen(false); setApprovalNotes(''); setWorkforceConfirmed(false); toast.success(input.decision === 'WITHDRAWN' ? 'Approval request withdrawn' : 'Review returned for changes') },
    onSettled: () => queryClient.invalidateQueries({ queryKey: trpc.accessReview.pathKey() }),
  })

  if (periodQuery.isLoading) {
    return <PageMessage>Loading review period…</PageMessage>
  }
  if (!period) {
    return <PageMessage>Access review period not found.</PageMessage>
  }

  const allApplicationsComplete =
    period.applicationCount > 0 &&
    period.completedApplicationCount === period.applicationCount
  const workforceDirty =
    workforceStatusQuery.isError || (workforceStatusQuery.data?.isDirty ?? true)
  const workforceCheckMessage = workforceStatusQuery.isPending
    ? 'Checking workforce…'
    : workforceStatusQuery.isError
      ? 'Workforce check failed'
      : workforceDirty ? 'Workforce update needed' : undefined
  const isSigned = period.status === 'SIGNED'
  const approvalPending = period.status === 'PENDING_APPROVAL'
  const selectedApprover = approverUserId || (period.defaultReviewerUserId !== signerUserId ? period.defaultReviewerUserId ?? '' : '')
  const isLegacy = period.periodKey.startsWith('legacy:')
  // A review that has not started — nothing completed, nothing decided — can
  // be deleted outright; anything further along is cancelled per application.
  const deletable =
    !isSigned &&
    period.completedApplicationCount === 0 &&
    period.resolvedCount === 0

  return (
    <div className='font-body mx-auto max-w-5xl px-6 py-7'>
      <PageHeaderBreadcrumb>
        <Link
          to='/workspaces/$workspaceId/reviews/access'
          params={{ workspaceId }}
          className='font-heading text-text-subtle text-[15px] font-medium hover:text-cobalt-strong'
        >
          Access Reviews
        </Link>
      </PageHeaderBreadcrumb>
      <PageHeaderExtra>
        <PeriodBadge
          status={!isSigned && !approvalPending && allApplicationsComplete && workforceCheckMessage ? 'OPEN' : period.status}
          label={!isSigned && !approvalPending && allApplicationsComplete ? workforceCheckMessage : undefined}
        />
        {canWorkspaceWrite && !approvalPending && (!isSigned || period.latestRevisionPeriodKey === period.periodKey) ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size='icon'
                variant='ghost'
                className='h-8 w-8'
                aria-label='Review actions'
              >
                <MoreHorizontal className='h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              {isSigned ? (
                <DropdownMenuItem
                  disabled={revisionMutation.isPending}
                  onSelect={(event) => {
                    event.preventDefault()
                    revisionMutation.mutate({ periodKey })
                  }}
                >
                  {revisionMutation.isPending ? 'Preparing revision…' : 'Edit review'}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => setEditOpen(true)}>
                  Edit details…
                </DropdownMenuItem>
              )}
              {deletable ? (
                <DropdownMenuItem
                  className='text-destructive focus:text-destructive'
                  onClick={() => setDeleteOpen(true)}
                >
                  Delete review…
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </PageHeaderExtra>

      <header className='mb-5'>
        <div className='flex items-start justify-between gap-6'>
          <h1 className='text-text-secondary text-[24px] font-semibold tracking-[-0.02em]'>
            {period.label}
          </h1>
        </div>
        {canWorkspaceWrite && !isSigned && !approvalPending ? (
          <EditAccessReviewDialog
            period={period}
            open={editOpen}
            onOpenChange={setEditOpen}
          />
        ) : null}
        <ConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title='Delete this review?'
          desc={`"${period.label}" and its ${period.applicationCount} application review${period.applicationCount === 1 ? '' : 's'} will be removed. Nothing has been decided or signed, so no evidence is affected.`}
          confirmText='Delete review'
          destructive
          isLoading={deleteMutation.isPending}
          handleConfirm={() => deleteMutation.mutate({ periodKey })}
        />
        <div className='mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
          <HeaderField label='Owner'>
            <PersonWithDate
              name={period.createdByName ?? period.createdBy}
              date={period.createdAt}
              dateLabel='started'
            />
          </HeaderField>
          <HeaderField label='Signed by'>
            {period.signedByName || period.signedAt ? (
              <PersonWithDate
                name={period.signedByName ?? 'Signed'}
                date={period.signedAt}
                dateLabel='signed'
              />
            ) : (
              <span className='text-text-subtle text-[12px]'>
                Not signed yet
              </span>
            )}
          </HeaderField>
          <HeaderField label='Applications'>
            <span className='flex items-center gap-2'>
              <Progress
                value={
                  period.applicationCount
                    ? (period.completedApplicationCount /
                        period.applicationCount) *
                      100
                    : 0
                }
                className='h-1.5 w-24'
                aria-label='Applications complete'
              />
              <span className='text-text-secondary text-[12px] tabular-nums'>
                {period.completedApplicationCount}/{period.applicationCount}{' '}
                complete
              </span>
            </span>
          </HeaderField>
          <HeaderField label='Due'>
            <span className='text-text-secondary text-[12px]'>
              {format(new Date(period.dueDate), 'MMM d, yyyy')}
            </span>
          </HeaderField>
        </div>
      </header>

      {isSigned ? (
        <InfoNote className='mb-5'>
          This signed version is read-only. Use Edit review to make corrections
          and submit them for approval again; the existing signed evidence stays
          unchanged. Access changes in third-party applications must still be
          applied separately.
        </InfoNote>
      ) : null}

      {period.revisionHistory.length > 1 ? (
        <section className='mb-4 rounded-md border border-border bg-card px-4 py-3'>
          <div className='text-sm font-medium'>Revision history</div>
          <p className='mt-1 text-xs text-text-secondary'>
            {isSigned ? 'Signed versions remain unchanged.' : 'You are editing a revision. The previous signed evidence remains in place until this revision is approved and signed.'}
          </p>
          <div className='mt-2 flex flex-wrap gap-3'>
            {period.revisionHistory.map((revision) => (
              <Link key={revision.periodKey}
                to='/workspaces/$workspaceId/reviews/access/period/$periodKey'
                params={{ workspaceId, periodKey: revision.periodKey }}
                className='text-xs text-cobalt-strong hover:underline'
                aria-current={revision.periodKey === period.periodKey ? 'page' : undefined}>
                Version {revision.revisionNumber} · {revision.status === 'SIGNED' ? 'Signed' : revision.status === 'PENDING_APPROVAL' ? 'Awaiting approval' : 'Draft'}
                {revision.signedAt ? ` · ${format(new Date(revision.signedAt), 'MMM d, yyyy')}` : ''}
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {isSigned ? <SignedStats period={period} /> : null}

      <LinkedTestCard
        period={period}
        workspaceId={workspaceId}
      />

      <ApplicationsStage
        period={period}
        workspaceId={workspaceId}
        canWrite={canWorkspaceWrite && !isSigned && !approvalPending}
      />

      {approvalPending && period.approvalRequestedAt ? (
        <section className='mt-6 space-y-4 rounded-md border bg-card p-5'>
          <h2 className='text-sm font-semibold'>Awaiting approval</h2>
          <p className='text-sm text-muted-foreground'>
            {period.approvalRequestedByName ?? 'A teammate'} requested approval from {period.approvedByName ?? 'the assigned approver'}. Editing is paused until this request is returned or withdrawn.
          </p>
          {period.approvalAttestationExceptions ? <p className='whitespace-pre-wrap text-sm'>Exceptions: {period.approvalAttestationExceptions}</p> : null}
          {canWorkspaceWrite && signerUserId === period.approvedByUserId ? <>
            <label className='flex items-start gap-2 text-sm'>
              <Checkbox checked={workforceConfirmed} onCheckedChange={(value) => setWorkforceConfirmed(value === true)} />
              {DEFAULT_ATTESTATION} Approving signs and freezes this review.
            </label>
            {workforceDirty ? <p className='text-sm text-destructive'>{workforceCheckMessage}. Return this review for changes before approving.</p> : null}
          </> : null}
          {canWorkspaceWrite && (signerUserId === period.approvedByUserId || signerUserId === period.approvalRequestedBy) ? <>
            <div className='flex flex-wrap items-center gap-3'>
              {signerUserId === period.approvedByUserId ? (
                <Button disabled={!workforceConfirmed || workforceDirty || approveMutation.isPending || returnMutation.isPending} onClick={() => approveMutation.mutate({ periodKey, requestedAt: period.approvalRequestedAt!, workforceConfirmed: true })}>
                  {approveMutation.isPending ? 'Signing…' : 'Approve & sign'}
                </Button>
              ) : null}
              <Button variant='outline' disabled={approveMutation.isPending || returnMutation.isPending} onClick={() => setReturnOpen(true)}>
                {signerUserId === period.approvalRequestedBy ? 'Withdraw request' : 'Return for changes'}
              </Button>
            </div>
            <ConfirmDialog
              open={returnOpen}
              onOpenChange={(open) => {
                if (returnMutation.isPending) return
                setReturnOpen(open)
                if (!open) setApprovalNotes('')
              }}
              title={signerUserId === period.approvalRequestedBy ? 'Withdraw approval request?' : 'Return review for changes?'}
              desc='This ends the pending approval request and reopens the review for editing. Give a reason so the change is recorded.'
              confirmText={signerUserId === period.approvalRequestedBy ? 'Confirm withdrawal' : 'Return review'}
              showInput
              inputRequired
              inputLabel='Reason'
              inputPlaceholder='Explain why this review needs changes'
              inputValue={approvalNotes}
              onInputChange={setApprovalNotes}
              isLoading={returnMutation.isPending}
              disabled={approveMutation.isPending}
              handleConfirm={() => returnMutation.mutate({ periodKey, requestedAt: period.approvalRequestedAt!, decision: signerUserId === period.approvalRequestedBy ? 'WITHDRAWN' : 'REJECTED', notes: approvalNotes.trim() })}
            />
          </> : null}
        </section>
      ) : null}
      {!approvalPending && period.approvalDecision && period.approvalDecision !== 'APPROVED' ? <InfoNote className='mt-4'>
        {period.approvalDecision === 'WITHDRAWN' ? 'Approval request withdrawn' : 'Returned for changes'}: {period.approvalNotes}
      </InfoNote> : null}

      {isSigned ? (
        <SignedEvidence period={period} workspaceId={workspaceId} />
      ) : isLegacy || approvalPending || period.applicationCount === 0 ? null : (
        <WrapUpStage
          period={period}
          workspaceId={workspaceId}
          canWrite={canWorkspaceWrite}
          allApplicationsComplete={allApplicationsComplete}
          workforceDirty={workforceDirty}
          workforceCheckMessage={workforceStatusQuery.isError ? workforceStatusQuery.error.message : workforceStatusQuery.isPending ? 'Checking the workforce baseline…' : undefined}
          workforceConfirmed={workforceConfirmed}
          attestationExceptions={attestationExceptions}
          approverUserId={selectedApprover}
          requesterUserId={signerUserId}
          onApproverUserId={setApproverUserId}
          isPending={completionMutation.isPending}
          confirmOpen={confirmOpen}
          onWorkforceConfirmed={setWorkforceConfirmed}
          onAttestationExceptions={setAttestationExceptions}
          onOpenConfirm={() => setConfirmOpen(true)}
          onCloseConfirm={() => setConfirmOpen(false)}
          onComplete={() =>
            completionMutation.mutate({
              periodKey,
              workforceConfirmed: true,
              approverUserId: selectedApprover,
              attestationExceptions: attestationExceptions.trim() || undefined,
            })
          }
        />
      )}
    </div>
  )
}

function ApplicationsStage({
  period,
  workspaceId,
  canWrite,
}: {
  period: AccessReviewPeriodDTO
  workspaceId: string
  canWrite: boolean
}) {
  const [reviewToDelete, setReviewToDelete] = useState<{ id: string; name: string } | null>(null)
  const queryClient = useQueryClient()
  const deleteReviewMutation = useMutation({
    ...trpc.accessReview.deleteApplicationReview.mutationOptions(),
    onMutate: async ({ cycleId }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: trpc.accessReview.workforceStatus.queryKey({ cycleId }) }),
        queryClient.cancelQueries({ queryKey: trpc.accessReview.workforceImpact.queryKey({ cycleId }) }),
      ])
    },
    onError: (error: Error) => toast.error(error.message),
    onSettled: async (_result, error, { cycleId: deletedId }) => {
      // Refresh the parent first. Broad invalidation would refetch the deleted
      // application's workforce queries before its observers can unmount.
      await queryClient.invalidateQueries({ queryKey: trpc.accessReview.period.queryKey({ periodKey: period.periodKey }) })
      const current = queryClient.getQueryData(trpc.accessReview.period.queryKey({ periodKey: period.periodKey }))
      const firstId = current?.applicationReviews[0]?.id
      const cycleId = error || firstId !== deletedId ? firstId : undefined
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: trpc.accessReview.periods.queryKey() }),
        queryClient.invalidateQueries({ queryKey: trpc.accessReview.launchApplications.queryKey() }),
        ...(cycleId ? [
          queryClient.invalidateQueries({ queryKey: trpc.accessReview.workforceStatus.queryKey({ cycleId }) }),
          queryClient.invalidateQueries({ queryKey: trpc.accessReview.workforceImpact.queryKey({ cycleId }) }),
        ] : []),
      ])
    },
    onSuccess: () => { setReviewToDelete(null); toast.success('Application review deleted') },
  })
  const reopenReviewMutation = useMutation({
    ...trpc.accessReview.reopenApplication.mutationOptions(),
    onError: (error: Error) => toast.error(error.message),
    onSettled: () => queryClient.invalidateQueries({ queryKey: trpc.accessReview.pathKey() }),
    onSuccess: async (_result, { cycleId }) => {
      toast.success('Application reopened for editing')
      await navigate({
        to: '/workspaces/$workspaceId/reviews/access/$cycleId',
        params: { workspaceId, cycleId },
      })
    },
  })
  const navigate = useNavigate()
  const [applicationToDelete, setApplicationToDelete] = useState<{
    id: string
    name: string
  } | null>(null)
  const applicationsQuery = useQuery(
    trpc.accessReview.launchApplications.queryOptions()
  )
  const addApplicationMutation = useMutation({
    ...trpc.accessReview.addApplicationReview.mutationOptions(),
    onError: (error: Error) => toast.error(error.message),
    onSuccess: async () => {
      toast.success('Application added. Open it to collect its accounts')
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: trpc.accessReview.period.queryKey({
            periodKey: period.periodKey,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: trpc.accessReview.periods.queryKey(),
        }),
        queryClient.invalidateQueries({
          queryKey: trpc.accessReview.launchApplications.queryKey(),
        }),
      ])
    },
  })
  const deleteApplicationMutation = useMutation({
    ...trpc.customApplication.archive.mutationOptions(),
    onError: (error: Error) => toast.error(error.message),
    onSuccess: async () => {
      setApplicationToDelete(null)
      toast.success('Custom application deleted')
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: trpc.accessReview.launchApplications.queryKey(),
        }),
        queryClient.invalidateQueries({
          queryKey: trpc.customApplication.list.queryKey(),
        }),
      ])
    },
  })
  // An application already in the period — open or completed — is not
  // offered again; only a cancelled one can be re-added. Match on the live
  // scope as well as the launch key: a reconnected integration keeps the
  // review's applicationKey but changes its scope id.
  const currentApplicationKeys = new Set(
    period.applicationReviews
      .filter((review) => review.status !== 'CANCELLED')
      .flatMap((review) => [
        review.applicationKey,
        review.scopeType && review.scopeId
          ? `${review.scopeType === 'ASSET' ? 'asset' : review.scopeType.toLowerCase()}:${review.scopeId}`
          : null,
      ])
      .filter((key): key is string => Boolean(key))
  )
  const availableApplications = (applicationsQuery.data ?? []).filter(
    (application) =>
      !currentApplicationKeys.has(applicationKey(application))
  )
  const addApplication = (application: {
    source: 'INTEGRATION' | 'CUSTOM' | 'LICENSE'
    id: string
  }) =>
    addApplicationMutation.mutateAsync({
      periodKey: period.periodKey,
      application,
    })

  return (
    <>
      <section className='border-border overflow-hidden rounded-md border bg-card'>
        <div className='text-text-subtle border-border border-b px-4 py-3 text-[10.5px] font-semibold tracking-[0.06em] uppercase'>
          Applications: review one at a time ·{' '}
          {period.completedApplicationCount}/{period.applicationCount} complete
          · {period.resolvedCount}/{period.itemCount} grants decided
        </div>
        {period.applicationReviews.length === 0 ? (
          <div className='text-text-subtle px-4 py-6 text-center text-[11px]'>
            No applications are in this review yet. Add one below, then open it
            to collect its accounts.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Application</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className='text-right'>Accounts</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Assigned reviewer</TableHead>
                <TableHead className='text-right'>
                  <span className='sr-only'>Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {period.applicationReviews.map((review) => {
                const complete = review.status === 'COMPLETED'
                const populationError = populationFailure(review)
                return (
                  <TableRow
                    key={review.id}
                    className='cursor-pointer'
                    onClick={() =>
                      navigate({
                        to: '/workspaces/$workspaceId/reviews/access/$cycleId',
                        params: { workspaceId, cycleId: review.id },
                      })
                    }
                  >
                    <TableCell className='text-text-secondary text-[13px] font-semibold'>
                      <span className='flex items-center gap-2'>
                        <AppLogo name={review.scopeName ?? review.name} />
                        {review.scopeName ?? review.name}
                      </span>
                    </TableCell>
                    <TableCell>
                      <SourceBadge scopeType={review.scopeType} />
                    </TableCell>
                    <TableCell className='text-text-subtle text-right text-[12px] tabular-nums'>
                      {review.populationState === 'POPULATED' || complete
                        ? (review.itemCount ?? 0)
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <ApplicationProgress
                        review={review}
                        populationError={populationError}
                      />
                    </TableCell>
                    <TableCell>
                      <span className='flex items-center gap-2'>
                        <MemberAvatar
                          person={{ name: review.reviewerName ?? 'Unassigned' }}
                          size='sm'
                        />
                        <span className='text-text-secondary text-[12px]'>
                          {review.reviewerName ?? 'Unassigned'}
                        </span>
                      </span>
                    </TableCell>
                    <TableCell className='text-right whitespace-nowrap'>
                      <div className='flex items-center justify-end gap-1'>
                        {canWrite && period.status !== 'SIGNED' ? <>
                          {review.scopeType === 'CUSTOM' && review.scopeId && <CustomRestConnectionButton app={{ id: review.scopeId, name: review.scopeName ?? review.name }} />}
                          {(complete || review.evidenceCleanupPending) ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button size='icon' variant='ghost' className='h-7 w-7'
                                  aria-label={review.evidenceCleanupPending ? `Retry cleanup for ${review.scopeName ?? review.name}` : `Reopen ${review.scopeName ?? review.name} for editing`}
                                  disabled={reopenReviewMutation.isPending || deleteReviewMutation.isPending}
                                  onClick={(event) => { event.stopPropagation(); reopenReviewMutation.mutate({ cycleId: review.id }) }}>
                                  {review.evidenceCleanupPending ? <RotateCcw className='h-3.5 w-3.5' /> : <Pencil className='h-3.5 w-3.5' />}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>{review.evidenceCleanupPending ? 'Retry cleanup' : 'Reopen for editing'}</TooltipContent>
                            </Tooltip>
                          ) : null}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button size='icon' variant='ghost' className='h-7 w-7 text-text-subtle hover:text-destructive'
                                aria-label={`Delete ${review.scopeName ?? review.name} review`}
                                disabled={reopenReviewMutation.isPending || deleteReviewMutation.isPending}
                                onClick={(event) => { event.stopPropagation(); setReviewToDelete({ id: review.id, name: review.scopeName ?? review.name }) }}>
                                <Trash2 className='h-3.5 w-3.5' />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete review</TooltipContent>
                          </Tooltip>
                        </> : null}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button asChild size='icon' variant='ghost' className='h-7 w-7'
                              aria-label={`View ${review.scopeName ?? review.name}`}>
                              <Link to='/workspaces/$workspaceId/reviews/access/$cycleId'
                                params={{ workspaceId, cycleId: review.id }} onClick={(event) => event.stopPropagation()}>
                                <Eye className='h-3.5 w-3.5' />
                              </Link>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>View review</TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </section>

      {canWrite ? (
        <section className='border-border mt-4 rounded-md border bg-card px-4 py-4'>
          <div className='flex items-start justify-between gap-4'>
            <div>
              <h2 className='text-text-secondary text-[12px] font-semibold'>
                Add an application
              </h2>
              <p className='text-text-subtle mt-1 text-[10px] leading-4'>
                Add a connected application, or register a custom application
                and create its review in this period.
              </p>
            </div>
            <AddCustomApplicationDialog
              triggerLabel='Add custom application'
              triggerVariant='outline'
              onCreated={(application) =>
                addApplication({ source: 'CUSTOM', id: application.id })
              }
            />
          </div>
          {applicationsQuery.isLoading ? (
            <p className='text-text-subtle mt-4 text-[11px]'>
              Loading applications…
            </p>
          ) : availableApplications.length > 0 ? (
            <div className='mt-4 space-y-4'>
              {APPLICATION_GROUPS.map((group) => {
                const items = availableApplications.filter(group.match)
                if (items.length === 0) return null
                return (
                  <div key={group.title}>
                    <div className='text-text-subtle text-[9px] font-medium tracking-[0.1em] uppercase'>
                      {group.title}
                    </div>
                    <div className='border-border mt-1 divide-y border-t'>
                      {items.map((application) => (
                        <div
                          key={`${application.source}:${application.id}`}
                          className='flex items-center gap-3 py-3'
                        >
                          <div className='min-w-0 flex-1'>
                            <div className='text-text-secondary flex items-center gap-2 text-[12px] font-medium'>
                              <AppLogo name={application.name} />
                              {application.name}
                            </div>
                          </div>
                          {application.source === 'CUSTOM' && <CustomRestConnectionButton app={application} />}
                          <SourceBadge
                            scopeType={
                              application.source === 'LICENSE'
                                ? 'ASSET'
                                : application.source
                            }
                          />
                          <Button
                            size='sm'
                            variant='outline'
                            disabled={addApplicationMutation.isPending}
                            onClick={() => {
                              void addApplication({
                                source: application.source,
                                id: application.id,
                              }).catch(() => undefined)
                            }}
                          >
                            Add review
                          </Button>
                          {application.source === 'CUSTOM' ? (
                            <Button
                              size='icon'
                              variant='ghost'
                              className='text-text-subtle hover:text-status-red hover:bg-status-red/10 h-8 w-8'
                              disabled={deleteApplicationMutation.isPending}
                              aria-label={`Delete ${application.name}`}
                              onClick={() =>
                                setApplicationToDelete({
                                  id: application.id,
                                  name: application.name,
                                })
                              }
                            >
                              <Trash2 className='h-3.5 w-3.5' />
                            </Button>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className='text-text-subtle mt-4 text-[11px]'>
              No other configured applications are available.
            </p>
          )}
        </section>
      ) : null}

      <ConfirmDialog open={Boolean(reviewToDelete)} onOpenChange={(open) => { if (!open) setReviewToDelete(null) }}
        title='Delete application review?' desc={`This will delete this ${reviewToDelete?.name ?? 'application'} review and its decisions. You can add the application again when needed.`}
        confirmText='Delete review' destructive isLoading={deleteReviewMutation.isPending}
        handleConfirm={() => { if (reviewToDelete) deleteReviewMutation.mutate({ cycleId: reviewToDelete.id }) }} />
      <ConfirmDialog
        open={Boolean(applicationToDelete)}
        onOpenChange={(open) => {
          if (!open) setApplicationToDelete(null)
        }}
        title='Delete custom application?'
        desc={`${applicationToDelete?.name ?? 'This application'} will no longer be available for future access reviews. Existing completed reviews and signed evidence remain unchanged.`}
        confirmText='Delete application'
        destructive
        isLoading={deleteApplicationMutation.isPending}
        handleConfirm={() => {
          if (applicationToDelete) {
            deleteApplicationMutation.mutate({ id: applicationToDelete.id })
          }
        }}
      />
    </>
  )
}

/** Collection state is derived server-side; a failed sync is never a spinner. */
function populationFailure(
  review: AccessReviewPeriodDTO['applicationReviews'][number]
) {
  return review.populationState === 'FAILED'
    ? (review.populationError ?? null)
    : null
}

function isCollectingPopulation(
  review: AccessReviewPeriodDTO['applicationReviews'][number]
) {
  return review.populationState === 'COLLECTING'
}

/**
 * Progress cell: a ring of decided / total grants once accounts exist;
 * before that, the collection state (not started / collecting / failed).
 */
function ApplicationProgress({
  review,
  populationError,
}: {
  review: AccessReviewPeriodDTO['applicationReviews'][number]
  populationError: { message: string } | null
}) {
  if (review.status === 'COMPLETED') {
    return (
      <span className='flex items-center gap-2'>
        <ProgressRing value={1} tone='green' />
        <span className='text-text-secondary text-[12px]'>
          Completed · {deniedSummary(review)}
        </span>
      </span>
    )
  }
  if (populationError) {
    return (
      <span className='flex flex-col gap-0.5'>
        <Badge status='red'>
          <AlertTriangle />
          Collection failed
        </Badge>
        <span
          className='text-status-red max-w-xs truncate text-[10px]'
          title={populationError.message}
        >
          {populationError.message}
        </span>
      </span>
    )
  }
  if (isCollectingPopulation(review)) {
    return (
      <Badge status='blue' aria-busy='true'>
        <LoaderCircle className='animate-spin' />
        Collecting…
      </Badge>
    )
  }
  if (review.populationState === 'NOT_STARTED') {
    return (
      <span className='flex items-center gap-2'>
        <ProgressRing value={0} />
        <span className='text-text-subtle text-[12px]'>
          Accounts not collected
        </span>
      </span>
    )
  }
  const total = review.itemCount ?? 0
  const decided = review.resolvedCount ?? 0
  return (
    <span className='flex items-center gap-2'>
      <ProgressRing value={total ? decided / total : 0} />
      <span className='text-text-secondary text-[12px] tabular-nums'>
        {decided}/{total} decided
      </span>
    </span>
  )
}

/** Small ring chart of a 0–1 fraction. */
/**
 * RFC-227 — how much of what this application flagged has actually been
 * removed. Reported only when something was denied; "0 denied" already says
 * there is nothing to remediate.
 */
function deniedSummary(review: {
  deniedCount?: number
  remediatedCount?: number
}): string {
  const denied = review.deniedCount ?? 0
  if (denied === 0) return '0 denied'
  return `${denied} denied · ${review.remediatedCount ?? 0} removed`
}

function ProgressRing({
  value,
  tone = 'ink',
}: {
  value: number
  tone?: 'ink' | 'green'
}) {
  const size = 18
  const stroke = 3
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const clamped = Math.max(0, Math.min(1, value))
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role='img'
      aria-label={`${Math.round(clamped * 100)}% complete`}
      className='shrink-0 -rotate-90'
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill='none'
        strokeWidth={stroke}
        className='stroke-border'
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill='none'
        strokeWidth={stroke}
        strokeLinecap='round'
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - clamped)}
        className={tone === 'green' ? 'stroke-status-green' : 'stroke-primary'}
      />
    </svg>
  )
}

/** "Add an application" groups: connected integrations first. */
const APPLICATION_GROUPS: Array<{
  title: string
  match: (application: { source: 'INTEGRATION' | 'CUSTOM' | 'LICENSE' }) => boolean
}> = [
  {
    title: 'Applications from integrations',
    match: (application) => application.source === 'INTEGRATION',
  },
  {
    title: 'Custom applications & license register',
    match: (application) => application.source !== 'INTEGRATION',
  },
]

function applicationKey(application: {
  source: 'INTEGRATION' | 'CUSTOM' | 'LICENSE'
  id: string
}) {
  return `${
    application.source === 'LICENSE'
      ? 'asset'
      : application.source.toLowerCase()
  }:${application.id}`
}

function SourceBadge({
  scopeType,
}: {
  scopeType: AccessReviewPeriodDTO['applicationReviews'][number]['scopeType']
}) {
  const label =
    scopeType === 'INTEGRATION'
      ? 'Integration'
      : scopeType === 'CUSTOM'
        ? 'Custom'
        : scopeType === 'ASSET'
          ? 'Asset'
          : 'Legacy'
  return (
    <span
      className={`w-fit rounded-full px-[9px] py-0.5 text-[10px] font-semibold tracking-wide uppercase ${
        scopeType === 'INTEGRATION'
          ? 'bg-status-green-badge-surface text-status-green-text-muted'
          : 'bg-status-gray-surface-subtle text-text-subtle'
      }`}
    >
      {label}
    </span>
  )
}

function WrapUpStage({
  period,
  canWrite,
  allApplicationsComplete,
  workforceDirty,
  workforceCheckMessage,
  workforceConfirmed,
  attestationExceptions,
  approverUserId,
  requesterUserId,
  onApproverUserId,
  isPending,
  confirmOpen,
  onWorkforceConfirmed,
  onAttestationExceptions,
  onOpenConfirm,
  onCloseConfirm,
  onComplete,
}: {
  period: AccessReviewPeriodDTO
  canWrite: boolean
  allApplicationsComplete: boolean
  workforceDirty: boolean
  workforceCheckMessage?: string
  workforceConfirmed: boolean
  attestationExceptions: string
  approverUserId: string
  requesterUserId: string
  onApproverUserId: (value: string) => void
  isPending: boolean
  confirmOpen: boolean
  onWorkforceConfirmed: (checked: boolean) => void
  onAttestationExceptions: (value: string) => void
  onOpenConfirm: () => void
  onCloseConfirm: () => void
  onComplete: () => void
}) {
  const membersQuery = useQuery(trpc.workspaceMembers.list.queryOptions())
  const members = (membersQuery.data ?? [])
    .filter((member) => member.user !== null && member.user.id !== requesterUserId && ['ADMIN', 'CONTRIBUTOR'].includes(member.role) && !member.user.isServiceAccount)
    .map((member) => ({ user: member.user! }))
  const canComplete =
    canWrite && allApplicationsComplete && workforceConfirmed && !workforceDirty && Boolean(approverUserId) && approverUserId !== requesterUserId
  return (
    <section className='mt-6'>
      <SectionTitle
        title='Request final approval'
        subtitle={
          allApplicationsComplete
            ? 'Every application is complete. Confirm the shared workforce and send the review to another person for approval.'
            : `Complete every application above first (${period.completedApplicationCount}/${period.applicationCount} done).`
        }
      />

      <div className='space-y-4'>
        {canWrite && period.applicationReviews[0] ? (
          <WorkforceRecovery
            cycleId={period.applicationReviews[0].id}
            enabled={!period.applicationReviews.some(isCollectingPopulation)}
          />
        ) : null}
        <section className='border-border rounded-md border bg-card px-4 py-4'>
          <div className='text-text-secondary text-[11px] font-semibold'>
            Workforce attestation
          </div>
          <p className='text-text-body mt-1 text-[11px] leading-5'>
            Confirm the frozen workforce snapshot shared by
            all {period.applicationCount} application reviews was complete and
            accurate for this period.
          </p>
          {workforceDirty ? (
            <div className='bg-status-orange-bg text-status-orange mt-3 rounded-md border border-status-orange-border px-3 py-2 text-[11px]'>
              {workforceCheckMessage ?? 'The workforce baseline has changed. Check the changes above before signing; only affected grants need another review.'}
            </div>
          ) : (
            <label className='text-text-secondary mt-3 flex cursor-pointer items-center gap-2 text-[12px]'>
              <Checkbox
                checked={workforceConfirmed}
                onCheckedChange={(value) =>
                  onWorkforceConfirmed(value === true)
                }
                disabled={!canWrite}
              />
              <span>
                I confirm this workforce list was complete and accurate for this
                period.
              </span>
            </label>
          )}
        </section>

        <section className='border-border rounded-md border bg-card px-4 py-4'>
          <div className='text-text-secondary text-[11px] font-semibold'>
            Attestation statement
          </div>
          <p className='border-border bg-surface-subtle text-text-body mt-2 rounded-md border px-3 py-3 text-[11px] leading-5'>
            “{DEFAULT_ATTESTATION}”
          </p>
          <label
                htmlFor='period-attestation-exceptions'
                className='text-text-secondary mt-4 block text-[11px] font-semibold'
              >
                Exceptions or notes (optional)
              </label>
              <textarea
                id='period-attestation-exceptions'
                rows={3}
                value={attestationExceptions}
                onChange={(event) =>
                  onAttestationExceptions(event.target.value)
                }
                disabled={!canWrite}
                maxLength={5000}
                placeholder='Explain which systems this review covers, any excluded systems and why, and any exceptions to the recorded decisions.'
                className='border-border bg-surface-subtle text-text-body mt-2 w-full rounded-md border px-3 py-2 text-[11px] leading-5 outline-none focus:border-ring disabled:opacity-70'
              />
          <div className='mt-4'>
            <span className='text-text-secondary block text-[11px] font-semibold'>
              Final approver
            </span>
            <p className='text-text-subtle mt-1 text-[10px] leading-4'>
              This person receives an approval task and notification. Only they can
              approve and sign; you can withdraw the request to make changes.
            </p>
            <div className='mt-2 max-w-xs'>
              <UserSelect
                value={approverUserId}
                onValueChange={onApproverUserId}
                members={members}
                disabled={!canWrite}
                placeholder='Choose an approver'
              />
            </div>
          </div>
        </section>

        <section
          className={`rounded-md border px-4 py-4 ${
            period.desiredStateChanges.length > 0
              ? 'bg-status-orange-bg border-status-orange-border'
              : 'border-border bg-card'
          }`}
        >
          <div className='text-text-secondary text-[11px] font-semibold'>
            Desired-state changes to apply in each application (
            {period.desiredStateChanges.length})
          </div>
          {period.desiredStateChanges.length > 0 ? (
            <div className='mt-2 divide-y divide-status-orange-border'>
              {period.desiredStateChanges.map((change) => (
                <div
                  key={change.id}
                  className='flex items-center gap-3 py-2 text-[11px]'
                >
                  <span className='text-text-body min-w-0 flex-1'>
                    {change.account} · {change.application}
                  </span>
                  <span className='text-status-orange rounded-md border border-status-orange-border bg-card px-2 py-0.5 text-[9px] uppercase'>
                    {change.decision}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className='text-text-subtle mt-1 text-[11px]'>
              No open desired-state changes in this period.
            </p>
          )}
          <p className='text-text-subtle mt-2 text-[10px] leading-4'>
            Screenata records the desired state; it does not change the
            third-party system. Open findings remain disclosed in the signed
            roll-up.
          </p>
        </section>
      </div>

      {canWrite ? (
        <div className='mt-5 flex justify-end'>
          <Button disabled={!canComplete || isPending} onClick={onOpenConfirm}>
            <ShieldCheck className='mr-1.5 h-4 w-4' />
            Request approval
          </Button>
        </div>
      ) : null}

      {confirmOpen ? (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4'>
          <div className='border-border w-full max-w-md rounded-md border bg-card p-5 shadow-xl'>
            <h3 className='text-text-secondary text-[14px] font-semibold'>
              Request approval for {period.label}?
            </h3>
            <p className='text-text-body mt-2 text-[11px] leading-5'>
              This sends an approval task and notification to the selected person and
              pauses editing. The review is signed only after they approve it.
            </p>
            {period.desiredStateChanges.length > 0 ? (
              <div className='bg-status-orange-bg mt-3 rounded-md border border-status-orange-border px-3 py-2'>
                <div className='text-status-orange text-[10px] font-semibold'>
                  {period.desiredStateChanges.length} desired-state change
                  {period.desiredStateChanges.length === 1 ? '' : 's'} to apply
                  in the applications
                </div>
                <ul className='text-text-body mt-1 space-y-1 text-[10px]'>
                  {period.desiredStateChanges.map((change) => (
                    <li key={change.id}>
                      • {change.account} · {change.application}:{' '}
                      {change.decision}
                    </li>
                  ))}
                </ul>
                <p className='text-text-subtle mt-2 text-[10px]'>
                  Signing does not execute these changes in third-party systems.
                </p>
              </div>
            ) : null}
            <div className='mt-4 flex justify-end gap-2'>
              <Button
                variant='ghost'
                size='sm'
                disabled={isPending}
                onClick={onCloseConfirm}
              >
                Cancel
              </Button>
              <Button size='sm' disabled={isPending} onClick={onComplete}>
                {isPending ? 'Requesting…' : 'Request approval'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}

/** Signed-review headline numbers, shown under the header. */
function SignedStats({ period }: { period: AccessReviewPeriodDTO }) {
  // The pack's cryptographic seal — distinct from the reviewer's sign-off in
  // the header. UNSIGNED means signing is not configured in this
  // environment, so it is neutral, not a warning.
  const status = period.periodEvidencePackSignatureStatus ?? 'SIGNED'
  const signature =
    status === 'TSA_VERIFIED'
      ? 'Verified'
      : status === 'UNSIGNED'
        ? 'Not configured'
        : status === 'VERIFICATION_FAILED'
          ? 'Failed'
          : 'Sealed'
  return (
    <div className='mb-6 grid grid-cols-2 gap-3 md:grid-cols-4'>
      <StatCard
        label='Digital seal'
        value={signature}
        valueClassName={
          status === 'VERIFICATION_FAILED'
            ? STAT_CARD_VALUE_COLORS.warning
            : status === 'UNSIGNED'
              ? undefined
              : STAT_CARD_VALUE_COLORS.success
        }
      />
      <StatCard label='Accounts' value={period.itemCount} />
      <StatCard
        label='Desired changes'
        value={period.desiredStateChanges.length}
        valueClassName={
          period.desiredStateChanges.length > 0
            ? STAT_CARD_VALUE_COLORS.warning
            : undefined
        }
      />
      <StatCard label='Applications' value={period.applicationCount} />
    </div>
  )
}

function SignedEvidence({
  period,
  workspaceId,
}: {
  period: AccessReviewPeriodDTO
  workspaceId: string
}) {
  return (
    <div className='mt-6 space-y-5'>
      <section className='border-border overflow-hidden rounded-md border bg-card'>
        <div className='border-border bg-surface-subtle text-text-secondary border-b px-4 py-3 text-[11px] font-semibold'>
          Application review packs
        </div>
        {period.applicationReviews.map((review) => (
          <div
            key={review.id}
            className='border-border flex items-center gap-4 border-t px-4 py-3 first:border-t-0'
          >
            <FileCheck2 className='h-4 w-4 text-status-green-deep' />
            <Link
              to='/workspaces/$workspaceId/reviews/access/$cycleId'
              params={{ workspaceId, cycleId: review.id }}
              className='text-text-secondary min-w-0 flex-1 text-[11px] font-medium hover:text-cobalt-strong'
            >
              {review.scopeName ?? review.name} · {review.itemCount ?? 0}{' '}
              grants · {deniedSummary(review)}
            </Link>
            <PeriodBadge status='SIGNED' />
            {review.evidencePackId ? (
              <EvidencePackActionsSection
                evidencePackId={review.evidencePackId}
                compact
                showCopy={false}
                downloadLabel='Download application pack'
              />
            ) : null}
          </div>
        ))}
      </section>

      <section className='border-border rounded-md border bg-card px-4 py-4'>
        <div className='text-text-secondary text-[11px] font-semibold'>
          Evidence
        </div>
        <div className='text-text-body mt-3 space-y-2 text-[11px]'>
          <ArtifactRow
            label='workforce-snapshot.json'
            detail='shared period baseline, explicitly confirmed'
          />
          <ArtifactRow
            label='decisions.csv'
            detail={`${period.itemCount} account rows with application, access level, decision, reviewer, timestamp, reason, and population run`}
          />
          <ArtifactRow
            label='desired-state.json'
            detail={`${period.desiredStateChanges.length} denied or modified grants to apply in the applications`}
          />
          <ArtifactRow
            label='summary.json'
            detail='scope, attestation, coverage disclosure, and exception counts'
          />
        </div>
        {period.periodEvidencePackId ? (
          <EvidencePackActionsSection
            evidencePackId={period.periodEvidencePackId}
            compact
            showCopy={false}
            downloadLabel='Download period roll-up'
            className='mt-4'
          />
        ) : null}
      </section>

    </div>
  )
}

function PersonWithDate({
  name,
  date,
  dateLabel,
}: {
  name: string
  date: Date | string | null
  dateLabel: string
}) {
  return (
    <span className='flex min-w-0 items-center gap-2'>
      <MemberAvatar person={{ name }} size='sm' />
      <span className='min-w-0'>
        <span className='text-text-secondary block truncate text-[12px] font-medium leading-4'>
          {name}
        </span>
        {date ? (
          <span className='text-text-subtle block text-[10px] leading-4'>
            {dateLabel} {format(new Date(date), 'MMM d, yyyy')}
          </span>
        ) : null}
      </span>
    </span>
  )
}

/** Signing links evidence; evidence review is handled separately. */
function LinkedTestCard({
  period,
  workspaceId,
}: {
  period: AccessReviewPeriodDTO
  workspaceId: string
}) {
  const signed = period.status === 'SIGNED'
  let evidenceStatus = 'evidence will be linked after final approval and signing if the Test is not submitted or approved.'
  if (signed) {
    evidenceStatus = period.evidenceLinked
      ? 'signed evidence is linked to this Test. Evidence review is separate.'
      : 'signed evidence is saved but not linked to this Test. If the Test is submitted or approved, Reset it first, then use Include to link this signed review.'
  }

  return (
    <section className='border-border mb-4 rounded-md border bg-card px-4 py-3'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='min-w-0'>
          <div className='text-text-subtle text-[9px] font-medium tracking-[0.1em] uppercase'>
            Linked Test
          </div>
          <div className='text-text-body mt-1 text-[12px] leading-5'>
            <Link
              to='/workspaces/$workspaceId/tests/$testId'
              params={{ workspaceId, testId: period.controlEvidenceStatusId }}
              className='font-semibold text-cobalt-strong hover:underline'
            >
              {period.anchorTestCode ?? 'Access Review Test'}
            </Link>
            {period.anchorTestTitle ? ` · ${period.anchorTestTitle}` : ''}
            {': '}
            {evidenceStatus}
            {signed && period.auditCycleId && period.auditCycleLabel ? (
              <>
                {' '}
                Pinned to{' '}
                <Link
                  to='/workspaces/$workspaceId/auditor-center/$auditCycleId/dashboard'
                  params={{ workspaceId, auditCycleId: period.auditCycleId }}
                  className='font-medium text-cobalt-strong hover:underline'
                >
                  AuditCycle “{period.auditCycleLabel}”
                </Link>
                .
              </>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  )
}

function SectionTitle({
  title,
  subtitle,
}: {
  title: string
  subtitle: string
}) {
  return (
    <div className='mb-3'>
      <h2 className='text-text-secondary text-[14px] font-semibold'>{title}</h2>
      <p className='text-text-subtle mt-0.5 text-[11px]'>{subtitle}</p>
    </div>
  )
}

function PeriodBadge({
  status,
  label,
}: {
  status: AccessReviewPeriodDTO['status']
  label?: string
}) {
  const done = status === 'SIGNED'
  const ready = status === 'READY_TO_SIGN'
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] leading-none font-semibold tracking-wide ${
        done
          ? 'bg-status-green-badge-surface text-status-green-text-muted'
          : ready
            ? 'bg-status-amber-badge-surface text-status-amber-text-muted'
            : 'bg-selected text-cobalt-strong'
      }`}
    >
      {label ?? status.replaceAll('_', ' ')}
    </span>
  )
}

function ArtifactRow({ label, detail }: { label: string; detail: string }) {
  return (
    <div className='border-border bg-surface-subtle flex items-center gap-3 rounded-md border px-3 py-2'>
      <span className='text-text-secondary font-mono text-[10px]'>{label}</span>
      <span className='text-text-subtle min-w-0 flex-1'>{detail}</span>
      <span className='rounded-md border border-status-green-border-subtle bg-status-green-surface-subtle px-1.5 py-0.5 text-[8px] text-status-green-deep uppercase'>
        sha-256
      </span>
    </div>
  )
}

function PageMessage({ children }: { children: string }) {
  return (
    <div className='text-text-subtle px-6 py-14 text-center text-[12px]'>
      {children}
    </div>
  )
}

function WorkforceRecovery({ cycleId, enabled }: { cycleId: string; enabled: boolean }) {
  const queryClient = useQueryClient()
  const impact = useQuery({
    ...trpc.accessReview.workforceImpact.queryOptions({ cycleId }),
    enabled,
    meta: { skipGlobalErrorHandler: true },
    retry: false,
    refetchOnWindowFocus: false,
  })
  const apply = useMutation({
    ...trpc.accessReview.reReconcileWorkforce.mutationOptions(),
    onError: (error: Error) => toast.error(error.message),
    onSettled: () => queryClient.invalidateQueries({ queryKey: trpc.accessReview.pathKey() }),
    onSuccess: (result) => toast.success(`Workforce updated. ${result.affectedGrants} of ${result.checkedGrants} grants need review.`),
  })
  if (!enabled || (!impact.error && !impact.data?.changes.length && !impact.data?.affectedGrants.length && !impact.data?.recoveryPending)) return null

  return <section className='space-y-3 rounded-md border bg-card p-4 text-sm'>
    {impact.error ? <>
      <p className='text-destructive'>{impact.error.message}</p>
      <Button size='sm' variant='outline' disabled={impact.isFetching || apply.isPending} onClick={async () => { await impact.refetch(); await queryClient.invalidateQueries({ queryKey: trpc.accessReview.workforceStatus.queryKey({ cycleId }) }) }}>
        {impact.isFetching ? 'Checking…' : 'Retry workforce check'}
      </Button>
    </> : null}
    {impact.data && !impact.error ? <>
      <p>{impact.data.changes.length} workforce changes · {impact.data.affectedGrants.length} of {impact.data.checkedGrants} grants affected</p>
      <ul className='space-y-1'>{impact.data.changes.map((change) => <li key={`${change.employeeId}:${change.email}`}>{change.email}: {change.reasons.join('; ')}</li>)}</ul>
      <ul className='space-y-1'>{impact.data.affectedGrants.map((grant) => <li key={grant.itemId}>{grant.application} · {grant.account}: {grant.reasons.join('; ')} (current decision: {grant.previousDecision})</li>)}</ul>
      <p className='text-muted-foreground'>{impact.data.affectedGrants.length > 0
        ? 'Applying these changes returns only the affected grants to pending. Other decisions keep their reviewer, date, and reason.'
        : impact.data.changes.length > 0
          ? 'No grants need another review. Update the workforce baseline and evidence to continue signing.'
          : 'The workforce baseline is up to date. Retry the evidence update to finish recovery.'}</p>
      <Button size='sm' disabled={apply.isPending || impact.isFetching} onClick={() => apply.mutate({ cycleId, expectedFingerprint: impact.data!.fingerprint })}>
        {apply.isPending ? 'Updating…' : impact.data.changes.length === 0 && impact.data.affectedGrants.length === 0 ? 'Retry evidence update' : impact.data.affectedGrants.length === 0 ? 'Update baseline and evidence' : 'Apply changes'}
      </Button>
    </> : null}
  </section>
}
